import React, { useEffect, useMemo, useState } from 'react';
import {
  FaArrowLeft,
  FaBriefcase,
  FaChevronDown,
  FaCog,
  FaClock,
  FaDollarSign,
  FaFileAlt,
  FaQuestionCircle,
  FaSearch,
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { ADMIN_ROUTES, adminPath } from '../constants/adminPortal';
import '../styles/AdminFAQ.css';
import { graphqlRequest } from '../utils/graphqlRequest';
import { SUPPORT_FAQS_PUBLISHED_QUERY } from '../graphql/supportFaq';
import { categoryLabelToFaqSlug } from '../utils/supportFaqCategoryMap';

const CATEGORY_META = {
  time: { label: 'Time Tracking', pillClass: 'faq-pill--time', icon: FaClock, iconBg: '#3b82f6' },
  jobs: { label: 'Jobs & Assignments', pillClass: 'faq-pill--jobs', icon: FaBriefcase, iconBg: '#7c3aed' },
  payments: { label: 'Payments', pillClass: 'faq-pill--payments', icon: FaDollarSign, iconBg: '#059669' },
  reports: { label: 'Reports', pillClass: 'faq-pill--reports', icon: FaFileAlt, iconBg: '#ea580c' },
  account: { label: 'Account & Settings', pillClass: 'faq-pill--account', icon: FaCog, iconBg: '#6366f1' },
};

const CATEGORY_CARDS = [
  { id: 'time', count: 12 },
  { id: 'jobs', count: 8 },
  { id: 'payments', count: 6 },
  { id: 'reports', count: 10 },
  { id: 'account', count: 9 },
];

const COMMON_QUESTIONS = [
  {
    id: 'q1',
    category: 'time',
    question: 'How do I clock in or out for a shift?',
    answer:
      'Open your dashboard and use the Clock In button when your shift starts. Use Clock Out when you finish. You can also use Manual Timesheet if you need to add or correct an entry.',
  },
  {
    id: 'q2',
    category: 'time',
    question: 'What if I forgot to clock in?',
    answer:
      'Use the manual timesheet or clock correction flow from your dashboard or Timesheet section. If your organisation requires approval, your manager will review the request.',
  },
  {
    id: 'q3',
    category: 'jobs',
    question: 'How do I accept or decline a job?',
    answer:
      'Go to My Job or your assigned tasks list, open the job card, and choose the available action (Accept / Decline) before the deadline shown.',
  },
  {
    id: 'q4',
    category: 'jobs',
    question: 'Where can I see my rostered tasks?',
    answer:
      'Rostered tasks appear on your dashboard and under Project or My Job depending on your role. Look for the “Rostered” badge on relevant items.',
  },
  {
    id: 'q5',
    category: 'payments',
    question: 'When will I receive my payment?',
    answer:
      'Payment schedules depend on your employer’s payroll cycle. Check Payslip for issued payments and contact your payroll team for specific dates.',
  },
  {
    id: 'q6',
    category: 'reports',
    question: 'How do I submit a work report?',
    answer:
      'Open Work Report from the sidebar, select the project or shift, attach photos or notes as required, and submit. You’ll see confirmation when it’s saved.',
  },
  {
    id: 'q7',
    category: 'reports',
    question: 'Can I edit a report after submitting?',
    answer:
      'If edits are allowed, use the same Work Report section and open the existing report. Otherwise contact your manager to request a correction.',
  },
  {
    id: 'q8',
    category: 'account',
    question: 'How do I change my password or enable two-factor authentication?',
    answer:
      'Go to Profile → Account Settings. Use Change Password for your password, and enable Two-Factor Authentication in the Security section.',
  },
];

function FAQ() {
  const navigate = useNavigate();
  const [openId, setOpenId] = useState(null);
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState(null);
  const [savedFaqs, setSavedFaqs] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await graphqlRequest(SUPPORT_FAQS_PUBLISHED_QUERY);
        const rows = data?.supportFaqsPublished || [];
        if (!cancelled) setSavedFaqs(rows);
      } catch {
        if (!cancelled) setSavedFaqs([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const allQuestions = useMemo(() => {
    const fromApi = savedFaqs.map((r) => ({
      id: `saved-${r.id}`,
      category: categoryLabelToFaqSlug(r.category),
      question: r.question,
      answer: r.answer,
    }));
    return [...COMMON_QUESTIONS, ...fromApi];
  }, [savedFaqs]);

  const startLiveChat = () => {
    window.dispatchEvent(new Event('admin-live-chat-open'));
  };

  const goSupportHome = () => {
    navigate(adminPath(ADMIN_ROUTES.HELP_SUPPORT));
  };

  const filteredQuestions = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allQuestions.filter((item) => {
      const catOk = !activeCategory || item.category === activeCategory;
      if (!q) return catOk;
      const text = `${item.question} ${item.answer} ${CATEGORY_META[item.category]?.label || ''}`.toLowerCase();
      return catOk && text.includes(q);
    });
  }, [search, activeCategory, allQuestions]);

  const categoryCounts = useMemo(() => {
    const counts = {};
    allQuestions.forEach((item) => {
      counts[item.category] = (counts[item.category] || 0) + 1;
    });
    return counts;
  }, [allQuestions]);

  return (
    <div className="faq-page">
      <header className="faq-hero">
        <div className="faq-hero-top">
          <button type="button" className="faq-back-btn" onClick={() => navigate(-1)} aria-label="Go back">
            <FaArrowLeft />
          </button>
          <div className="faq-hero-text">
            <h1>Frequently Asked Questions</h1>
            <p>Find answers to common questions about using the platform.</p>
          </div>
          <div className="faq-hero-search">
            <FaSearch className="faq-search-icon" aria-hidden />
            <input
              type="search"
              placeholder="Search help topics"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search help topics"
            />
          </div>
        </div>
      </header>

      <section className="faq-section">
        <h2 className="faq-section-title">Browse by Category</h2>
        <div className="faq-category-grid">
          {CATEGORY_CARDS.map((card) => {
            const meta = CATEGORY_META[card.id];
            const Icon = meta.icon;
            const count = categoryCounts[card.id] ?? card.count;
            const selected = activeCategory === card.id;
            return (
              <button
                key={card.id}
                type="button"
                className={`faq-category-card ${selected ? 'active' : ''}`}
                onClick={() => setActiveCategory(selected ? null : card.id)}
              >
                <span className="faq-category-icon" style={{ background: meta.iconBg }}>
                  <Icon aria-hidden />
                </span>
                <span className="faq-category-label">{meta.label}</span>
                <span className="faq-category-count">{count} articles</span>
              </button>
            );
          })}
        </div>
        {activeCategory ? (
          <button type="button" className="faq-clear-filter" onClick={() => setActiveCategory(null)}>
            Show all categories
          </button>
        ) : null}
      </section>

      <section className="faq-section faq-accordion-section">
        <div className="faq-accordion-head">
          <FaQuestionCircle className="faq-accordion-icon" aria-hidden />
          <h2 className="faq-section-title">Common Questions</h2>
        </div>
        <ul className="faq-accordion-list">
          {filteredQuestions.length === 0 ? (
            <li className="faq-empty">No questions match your search. Try different keywords or clear filters.</li>
          ) : (
            filteredQuestions.map((item) => {
              const meta = CATEGORY_META[item.category];
              const expanded = openId === item.id;
              return (
                <li key={item.id} className={`faq-accordion-item ${expanded ? 'open' : ''}`}>
                  <button
                    type="button"
                    className="faq-accordion-trigger"
                    aria-expanded={expanded}
                    onClick={() => setOpenId(expanded ? null : item.id)}
                  >
                    <div className="faq-accordion-trigger-text">
                      <span className="faq-question-title">{item.question}</span>
                      <span className={`faq-pill ${meta.pillClass}`}>{meta.label}</span>
                    </div>
                    <FaChevronDown className="faq-chevron" aria-hidden />
                  </button>
                  {expanded ? (
                    <div className="faq-accordion-panel" role="region">
                      <p>{item.answer}</p>
                    </div>
                  ) : null}
                </li>
              );
            })
          )}
        </ul>
      </section>

      <section className="faq-cta-band" aria-labelledby="faq-cta-heading">
        <FaQuestionCircle className="faq-cta-icon" aria-hidden />
        <h2 id="faq-cta-heading">Still need help?</h2>
        <p>Can&apos;t find what you&apos;re looking for? Our support team is here to assist you.</p>
        <div className="faq-cta-actions">
          <button type="button" className="faq-cta-primary" onClick={goSupportHome}>
            Create Support Ticket
          </button>
          <button type="button" className="faq-cta-secondary" onClick={startLiveChat}>
            Start Live Chat
          </button>
        </div>
      </section>

    </div>
  );
}

export default FAQ;
